import { useState, useContext, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import { ThemeContext } from '../Context';
import { GuardiaAccessChecker } from './GuardiaAccessChecker';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SubjectIcon from '@mui/icons-material/Subject';
import NotesIcon from '@mui/icons-material/Notes';
import ShortTextIcon from '@mui/icons-material/ShortText';
import { ReactComponent as GuardiaLogo } from '../../assets/logo_guardia.svg';
import { logAction, LOG_ACTIONS, contentLengthLog } from '../../utils/logClient';

// Doit rester aligné sur SYNTHESE_MAX_LENGTH (backend/guardia/guardiaController.js)
const MAX_LENGTH = 30000;

const LEVELS = [
  {
    value: 'detaillee',
    label: 'Détaillée',
    Icon: SubjectIcon,
    description: 'Relate le déroulement des faits et les éléments utiles, en un texte synthétisé.',
  },
  {
    value: 'essentielle',
    label: 'Essentielle',
    Icon: NotesIcon,
    description: 'Résume les faits principaux en deux ou trois phrases.',
  },
  {
    value: 'courte',
    label: 'Courte',
    Icon: ShortTextIcon,
    description: 'Résume l\'ensemble des faits en une seule phrase.',
  },
];

const formatCount = (value) => value.toLocaleString('fr-BE');

function GuardiaSynthese() {
  const { theme } = useContext(ThemeContext);

  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';
  const accent = isDark ? '#c084fc' : '#800080';

  const [sourceText, setSourceText] = useState('');
  const [level, setLevel] = useState('essentielle');
  const [result, setResult] = useState(null); // { text, level }
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [guardiaAccess, setGuardiaAccess] = useState({ hasAccess: false, reason: '' });
  const [accessChecked, setAccessChecked] = useState(false);

  // Vérifier l'accès GuardIA au chargement
  useEffect(() => {
    const checkAccess = async () => {
      const access = await GuardiaAccessChecker.checkFeatureAccess('synthese');
      setGuardiaAccess(access);
      setAccessChecked(true);
    };

    checkAccess();
  }, []);

  const textLength = sourceText.trim().length;
  const isTooLong = textLength > MAX_LENGTH;
  const canSubmit = guardiaAccess.hasAccess && textLength > 0 && !isTooLong && !loading;
  const selectedLevel = LEVELS.find((option) => option.value === level);
  const resultLevel = result ? LEVELS.find((option) => option.value === result.level) : null;

  // Gestionnaire pour lancer la synthèse
  const handleSynthesize = async () => {
    if (!guardiaAccess.hasAccess) {
      setError(guardiaAccess.reason);
      return;
    }

    if (!canSubmit) return;

    const text = sourceText.trim();
    const requestedLevel = level;

    setLoading(true);
    setError('');
    setResult(null);
    setCopied(false);

    try {
      const token = sessionStorage.getItem('authToken');
      const response = await fetch('/api/guardia/synthese', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text, level: requestedLevel })
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        setResult({ text: data.synthese, level: requestedLevel });

        logAction(LOG_ACTIONS.GUARDIA_SYNTHESE, null, {
          niveau: requestedLevel,
          ...contentLengthLog(data.synthese || '', text)
        });
      } else {
        setError(data.message || 'Erreur lors de la synthèse du texte.');
      }
    } catch (err) {
      console.error('Erreur lors de l\'appel à l\'API GuardIA:', err);
      setError('Erreur de connexion au service GuardIA. Vérifiez que le serveur backend est démarré.');
    } finally {
      setLoading(false);
    }
  };

  // Ctrl + Entrée pour lancer la synthèse depuis la zone de texte
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSynthesize();
    }
  };

  // Gestionnaire pour repartir d'un texte vide
  const handleReset = () => {
    setSourceText('');
    setResult(null);
    setError('');
    setCopied(false);
  };

  // Gestionnaire pour copier la relation des faits
  const handleCopy = async () => {
    if (!result?.text) return;

    try {
      await navigator.clipboard.writeText(result.text);
      setCopied(true);
      // Reset l'état "copié" après 2 secondes
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  // Si l'accès n'est pas encore vérifié, afficher un loader
  if (!accessChecked) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDark ? '#1a252f' : '#f8f9fa',
        }}
      >
        <CircularProgress sx={{ color: isDark ? '#4fc3f7' : 'primary.main' }} />
      </Box>
    );
  }

  // Si l'accès n'est pas autorisé, afficher un message d'erreur
  if (!guardiaAccess.hasAccess) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDark ? '#1a252f' : '#f5f5f5',
          p: 3
        }}
      >
        <Alert
          severity="warning"
          sx={{
            backgroundColor: isDark ? 'rgba(255, 193, 7, 0.15)' : undefined,
            color: isDark ? '#ffc107' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#ffc107' : undefined
            },
            maxWidth: 400,
            textAlign: 'center'
          }}
        >
          {guardiaAccess.reason}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'auto',
        backgroundColor: isDark ? '#1a252f' : '#f8f9fa',
        p: 1.5,
      }}
    >
      {/* Section supérieure - Texte source et niveau de synthèse */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2,
          backgroundColor: isDark ? 'rgba(52, 73, 94, 0.7)' : 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
          width: '100%',
          maxWidth: '95%',
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 500,
            color: isDark ? '#ecf0f1' : 'text.primary',
            textAlign: 'center',
            mb: 1
          }}
        >
          <strong>GuardIA</strong> vous aide à rédiger une relation des faits à partir d'une audition ou d'un texte long.
        </Typography>

        <TextField
          multiline
          fullWidth
          minRows={6}
          maxRows={14}
          value={sourceText}
          onChange={(e) => setSourceText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          placeholder="Collez ici l'audition ou le texte à synthétiser..."
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDark ? '#22303e' : 'white',
              color: isDark ? '#ecf0f1' : '#333',
              fontSize: '0.8rem',
              lineHeight: 1.5,
              p: 1,
              '& fieldset': {
                borderColor: isTooLong ? '#f44336' : (isDark ? '#34495e' : '#ddd'),
              },
              '&:hover fieldset': {
                borderColor: isTooLong ? '#f44336' : accent,
              },
              '&.Mui-focused fieldset': {
                borderColor: isTooLong ? '#f44336' : accent,
                borderWidth: 1,
              },
              '&.Mui-disabled': {
                opacity: 0.7,
              },
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: isDark ? '#bdc3c7' : 'rgba(0, 0, 0, 0.6)',
            },
            '& .MuiInputBase-input::placeholder': {
              color: isDark ? '#95a5a6' : 'text.secondary',
              opacity: 1,
            },
          }}
        />

        {/* Compteur de caractères et effacement */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mt: 0.5,
            minHeight: 24,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              fontWeight: isTooLong ? 600 : 400,
              color: isTooLong ? '#f44336' : (isDark ? '#95a5a6' : 'text.secondary'),
            }}
          >
            {formatCount(textLength)} / {formatCount(MAX_LENGTH)} caractères
            {isTooLong && ' — limite dépassée'}
          </Typography>

          {sourceText && !loading && (
            <Button
              size="small"
              onClick={handleReset}
              sx={{
                color: isDark ? '#95a5a6' : 'text.secondary',
                fontSize: '0.7rem',
                textTransform: 'none',
                px: 1,
                py: 0.25,
                minWidth: 'auto',
                minHeight: 'auto',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                },
              }}
            >
              Effacer
            </Button>
          )}
        </Box>

        {/* Choix du niveau de synthèse */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            mb: 0.5,
            fontWeight: 600,
            fontSize: '0.75rem',
            color: isDark ? '#ecf0f1' : 'text.primary',
          }}
        >
          Niveau de synthèse
        </Typography>

        <ToggleButtonGroup
          value={level}
          exclusive
          fullWidth
          size="small"
          disabled={loading}
          onChange={(e, value) => {
            if (value) setLevel(value);
          }}
          sx={{
            '& .MuiToggleButton-root': {
              flex: 1,
              flexDirection: 'column',
              gap: 0.25,
              py: 0.75,
              px: 0.5,
              textTransform: 'none',
              fontSize: '0.72rem',
              fontWeight: 500,
              lineHeight: 1.1,
              color: isDark ? '#bdc3c7' : 'text.secondary',
              backgroundColor: isDark ? '#22303e' : 'white',
              borderColor: isDark ? '#34495e' : '#ddd',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(192, 132, 252, 0.08)' : 'rgba(128, 0, 128, 0.05)',
              },
              '&.Mui-selected': {
                color: accent,
                fontWeight: 600,
                backgroundColor: isDark ? 'rgba(192, 132, 252, 0.15)' : 'rgba(128, 0, 128, 0.08)',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(192, 132, 252, 0.22)' : 'rgba(128, 0, 128, 0.12)',
                },
              },
              '&.Mui-disabled': {
                color: isDark ? '#7f8c8d' : 'grey.500',
              },
            },
          }}
        >
          {LEVELS.map(({ value, label, Icon }) => (
            <ToggleButton key={value} value={value}>
              <Icon sx={{ fontSize: '1.1rem' }} />
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.75,
            fontSize: '0.7rem',
            lineHeight: 1.3,
            color: isDark ? '#bdc3c7' : 'text.secondary',
          }}
        >
          {selectedLevel?.description}
        </Typography>

        {/* Bouton Synthétiser */}
        <Box sx={{ textAlign: 'center', mt: 1.5, mb: 0.5 }}>
          <Button
            variant="contained"
            onClick={handleSynthesize}
            disabled={!canSubmit}
            sx={{
              backgroundColor: accent,
              color: 'white',
              px: 2.5,
              py: 0.75,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.8rem',
              '&:hover': {
                backgroundColor: isDark ? '#a855f7' : '#6b0050',
              },
              '&:disabled': {
                backgroundColor: isDark ? '#2d3f4c' : 'grey.300',
                color: isDark ? '#7f8c8d' : 'grey.500',
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Synthèse en cours...
              </>
            ) : (
              'Synthétiser'
            )}
          </Button>
        </Box>

        {/* Message d'erreur */}
        {error && (
          <Box
            sx={{
              mt: 1.5,
              p: 1,
              backgroundColor: isDark ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)',
              border: `1px solid ${isDark ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)'}`,
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#f44336',
                fontSize: '0.7rem',
                fontWeight: 500,
              }}
            >
              {error}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Relation des faits générée */}
      {result && (
        <Paper
          elevation={0}
          sx={{
            mt: 1.5,
            p: 1.5,
            width: '100%',
            maxWidth: '95%',
            flexShrink: 0,
            backgroundColor: isDark ? 'rgba(192, 132, 252, 0.1)' : 'rgba(128, 0, 128, 0.05)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isDark ? 'rgba(192, 132, 252, 0.3)' : 'rgba(128, 0, 128, 0.3)'}`,
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.75,
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <GuardiaLogo
                  style={{
                    width: '20px',
                    height: '20px',
                    flexShrink: 0,
                    color: accent
                  }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: accent,
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Relation des faits
                </Typography>
              </Box>

              {resultLevel && (
                <Chip
                  label={resultLevel.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: accent,
                    borderColor: isDark ? 'rgba(192, 132, 252, 0.5)' : 'rgba(128, 0, 128, 0.4)',
                  }}
                />
              )}
            </Box>

            <Button
              size="small"
              onClick={handleCopy}
              startIcon={copied
                ? <CheckIcon sx={{ fontSize: '0.95rem !important' }} />
                : <ContentCopyIcon sx={{ fontSize: '0.95rem !important' }} />}
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                px: 1,
                py: 0.25,
                minHeight: 'auto',
                borderRadius: 1.5,
                color: copied ? (isDark ? '#4ade80' : '#22c55e') : accent,
                transition: 'color 0.2s ease',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(192, 132, 252, 0.12)' : 'rgba(128, 0, 128, 0.08)',
                },
              }}
            >
              {copied ? 'Copié' : 'Copier'}
            </Button>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: isDark ? '#ecf0f1' : 'text.primary',
              lineHeight: 1.5,
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
            }}
          >
            {result.text}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1.5,
              pt: 1,
              borderTop: `1px solid ${isDark ? 'rgba(192, 132, 252, 0.2)' : 'rgba(128, 0, 128, 0.15)'}`,
              fontSize: '0.68rem',
              lineHeight: 1.35,
              fontStyle: 'italic',
              color: isDark ? '#95a5a6' : 'text.secondary',
            }}
          >
            Proposition générée par GuardIA sur la seule base du texte fourni. Relisez-la et vérifiez-la au regard du texte source avant toute utilisation.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default GuardiaSynthese;
